import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, X, Clock, ChevronDown, ChevronUp, Pencil, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
// ISO (UTC) -> value for <input type="datetime-local"> in local time
const toLocalInput = (iso) => {
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export const ScheduledEmailsBanner = ({ customerId, refreshKey, compact = false }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(!compact);
  const [editing, setEditing] = useState(null);
  const [editAt, setEditAt] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const { data } = await api.get(`/customers/${customerId}/scheduled-emails`, { params: { status: 'scheduled' } });
      setItems(data || []);
    } catch { setItems([]); }
  }, [customerId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const cancel = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/customers/${customerId}/scheduled-emails/${id}`);
      toast.success('Scheduled email cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(null); }
  };

  const saveReschedule = async (id) => {
    if (!editAt) return;
    setBusy(id);
    try {
      await api.patch(`/customers/${customerId}/scheduled-emails/${id}`, { scheduled_at: new Date(editAt).toISOString() });
      toast.success('Email rescheduled');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reschedule'); }
    finally { setBusy(null); }
  };

  if (!items.length) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm" data-testid="scheduled-emails-banner">
      <button type="button" className="w-full flex items-center gap-2 text-amber-900 dark:text-amber-200" onClick={() => setOpen(o => !o)}>
        <Calendar className="w-4 h-4 flex-shrink-0" />
        <span className="font-semibold">{items.length} scheduled email{items.length > 1 ? 's' : ''}</span>
        <span className="text-xs text-amber-700 dark:text-amber-300">· next {fmt(items[0]?.scheduled_at)}</span>
        <span className="ml-auto">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded border border-amber-200 px-2 py-1.5" data-testid={`scheduled-email-${it.id}`}>
              <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-xs">{it.subject}</p>
                {editing === it.id ? (
                  <div className="flex items-center gap-1 mt-1">
                    <Input type="datetime-local" value={editAt} onChange={(e) => setEditAt(e.target.value)} className="h-7 text-xs" data-testid={`reschedule-input-${it.id}`} />
                    <Button size="sm" className="h-7 text-xs" disabled={busy === it.id} onClick={() => saveReschedule(it.id)} data-testid={`reschedule-save-${it.id}`}>Save</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500">Sends {fmt(it.scheduled_at)}</p>
                )}
              </div>
              {editing !== it.id && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Reschedule" disabled={busy === it.id}
                    onClick={() => { setEditing(it.id); setEditAt(toLocalInput(it.scheduled_at)); }} data-testid={`reschedule-btn-${it.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" title="Cancel send" disabled={busy === it.id}
                    onClick={() => cancel(it.id)} data-testid={`cancel-scheduled-${it.id}`}>
                    {busy === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
